import type { FilterParam } from '@gov-nx/core/types';
import { usePpdfSouhlasyLocale } from '@gov-nx/module/page';
import {
	toDate,
	toDateReadable,
	useBoolean,
	useFilter,
} from '@gov-nx/utils/common';
import type { PageCode } from '../../../definitions/codes';
import type {
	PpdfConsentsFilterType,
	PpdfConsentsFormData,
	PpdfSouhlasySeznamContext,
} from '../context.types';

interface PpdfConsentsFiltersProps {
	code: PageCode;
	formMethods: PpdfSouhlasySeznamContext['formDefinition']['formMethods'];
	initialState: PpdfConsentsFilterType;
}

export const usePpdfConsentsFilters = ({
	formMethods,
	code,
	initialState,
}: PpdfConsentsFiltersProps) => {
	const { t } = usePpdfSouhlasyLocale();
	const filterWindowOpened = useBoolean(false);

	const { filter, setPartialFilter } = useFilter<PpdfConsentsFilterType>({
		key: code,
		initialState,
		rehydrate: (data) => ({
			...data,
			od: typeof data.od === 'string' ? toDate(data.od) : data.od,
			do: typeof data.do === 'string' ? toDate(data.do) : data.do,
		}),
	});

	const goToPage = (page: number) => {
		setPartialFilter({ start: (page - 1) * filter.pageSize });
	};

	const submitFilters = (values: PpdfConsentsFormData) => {
		setPartialFilter({ ...values, start: 0 });
		filterWindowOpened.setFalse();
	};

	const resetFilter = () => {
		formMethods.setValue('od', undefined);
		formMethods.setValue('do', undefined);
		formMethods.setValue('text', undefined);
		setPartialFilter({
			od: undefined,
			do: undefined,
			text: undefined,
		});
		filterWindowOpened.setFalse();
	};

	const filterParams: FilterParam[] = [
		{
			label: t('filtr.od.label'),
			value: filter.od ? toDateReadable(filter.od) : undefined,
			isActive: !!filter.od,
			onRemove: () => {
				setPartialFilter({ od: undefined });
				formMethods.resetField('od');
			},
		},
		{
			label: t('filtr.do.label'),
			value: filter.do ? toDateReadable(filter.do) : undefined,
			isActive: !!filter.do,
			onRemove: () => {
				setPartialFilter({ do: undefined });
				formMethods.resetField('do');
			},
		},
	];

	const onFilterWindowClose = () => {
		formMethods.setValue('od', filter.od);
		formMethods.setValue('do', filter.do);
		filterWindowOpened.setFalse();
	};

	const isNotFirstPage = (filter: PpdfConsentsFilterType) => filter.start !== 0;

	const isEmpty = <T>(list?: T[]) => list?.length === 0;

	return {
		setPartialFilter,
		submitFilters,
		goToPage,
		filter,
		filterWindowOpened,
		onFilterWindowClose,
		resetFilter,
		filterParams,
		isNotFirstPage,
		isEmpty,
		pageNumber: filter.start / filter.pageSize + 1,
	};
};
